import React, { useState, useRef } from 'react'
import { useMutation, useQuery, gql } from '@apollo/client'
import { validateFunc } from '../../constraints/constraints'
import { withTranslation } from 'react-i18next'
import {
  editSection,
  restaurantList,
  createSection,
  getSections
} from '../../apollo'
import useStyles from './styles'
import useGlobalStyles from '../../utils/globalStyles'
import FormCardHeader from '../FormCardHeader'
import { Box, Switch, Typography, Input, Alert, Button } from '@mui/material'
import SearchRounded from '@mui/icons-material/SearchRounded'
import StorefrontOutlined from '@mui/icons-material/StorefrontOutlined'
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded'
import RadioButtonUncheckedRounded from '@mui/icons-material/RadioButtonUncheckedRounded'

const CREATE_SECTION = gql`
  ${createSection}
`
const EDIT_SECTION = gql`
  ${editSection}
`
const GET_SECTIONS = gql`
  ${getSections}
`
const GET_RESTAURANT = gql`
  ${restaurantList}
`

function Section(props) {
  const formRef = useRef()
  const name = props.section ? props.section.name : ''
  const mutation = props.section ? EDIT_SECTION : CREATE_SECTION
  const [sectionEnable, setSectionEnable] = useState(
    props.section ? props.section.enabled : false
  )
  const [restaurant, restaurantSetter] = useState(
    props.section ? props.section.restaurants.map(r => r._id) : []
  )
  const [error, errorSetter] = useState('')
  const [storeSearch, setStoreSearch] = useState('')
  const [success, successSetter] = useState('')
  const [nameError, nameErrorSetter] = useState(null)

  const onCompleted = data => {
    const message = props.section
      ? t('SectionUpdatedSuccessfully')
      : t('SectionAddeduccessfully')
    successSetter(message)
    errorSetter('')
    if (!props.section) clearFields()
  }
  function onError(error) {
    const message = `${t('ActionFailedTryAgain')} ${error}`
    successSetter('')
    errorSetter(message)
  }
  const [mutate, { loading }] = useMutation(mutation, {
    refetchQueries: [{ query: GET_SECTIONS }],
    onCompleted,
    onError
  })

  const {
    data,
    error: errorQuery,
    loading: loadingQuery
  } = useQuery(GET_RESTAURANT, { onError })
  const searchTerm = storeSearch.trim().toLowerCase()
  const visibleStores = ((data && data.restaurantList) || []).filter(
    store =>
      !searchTerm ||
      `${store.name} ${store.address || ''}`.toLowerCase().includes(searchTerm)
  )

  const toggleStore = id =>
    restaurantSetter(current =>
      current.includes(id)
        ? current.filter(item => item !== id)
        : [...current, id]
    )

  const onBlur = (setter, field, state) => {
    setter(!validateFunc({ [field]: state }, field))
  }
  const onSubmitValidation = () => {
    const nameErrors = !validateFunc(
      { name: formRef.current['input-name'].value },
      'name'
    )
    nameErrorSetter(nameErrors)
    return nameErrors
  }
  const clearFields = () => {
    formRef.current.reset()
    nameErrorSetter(null)
  }

  const { t } = props

  const classes = useStyles()
  const globalClasses = useGlobalStyles()
  return (
    <Box container className={classes.container}>
      <FormCardHeader
        className={classes.heading}
        title={props.section ? t('EditSection') : t('AddSection')}
        onClose={props.onClose}
        action={
          <Box className={globalClasses.flexRow} sx={{ gap: 0.5 }}>
            <label>{sectionEnable ? t('Disable') : t('Enable')}</label>
            <Switch
              defaultChecked={sectionEnable}
              value={sectionEnable}
              onChange={e => setSectionEnable(e.target.checked)}
              id="input-enable"
              name="input-enable"
              style={{ color: 'black' }}
            />
          </Box>
        }
      />

      <Box className={classes.form}>
        <form ref={formRef}>
          <Box className={globalClasses.flexRow}>
            <Input
              id="input-name"
              name="input-name"
              placeholder={t('SectionName')}
              type="text"
              defaultValue={name}
              onBlur={event => {
                onBlur(nameErrorSetter, 'name', event.target.value)
              }}
              disableUnderline
              className={[
                globalClasses.input,
                nameError === false
                  ? globalClasses.inputError
                  : nameError === true
                  ? globalClasses.inputSuccess
                  : ''
              ]}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flexWrap: 'wrap',
              mt: 2.5,
              mb: 1.5
            }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
              {t('Stores')}
            </Typography>
            <Box
              component="span"
              sx={{
                px: 1,
                py: 0.25,
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 700,
                bgcolor: 'primary.light',
                color: 'primary.main'
              }}>
              {restaurant.length} {t('Selected')}
            </Box>
            <Box sx={{ flexGrow: 1 }} />
            <Input
              placeholder={t('SearchStores')}
              value={storeSearch}
              onChange={e => setStoreSearch(e.target.value)}
              disableUnderline
              className={globalClasses.input}
              sx={{ mt: 0, width: { xs: '100%', sm: 240 }, height: 38 }}
              startAdornment={
                <SearchRounded
                  sx={{ fontSize: 18, color: 'text.disabled', mr: 1 }}
                />
              }
            />
            {restaurant.length ? (
              <Button
                size="small"
                onClick={() => restaurantSetter([])}
                sx={{ textTransform: 'none', fontWeight: 600 }}>
                {t('Clear')}
              </Button>
            ) : null}
          </Box>
          {loadingQuery ? (
            <Typography sx={{ fontSize: 13, color: 'text.disabled' }}>
              {t('LoadingDots')}
            </Typography>
          ) : null}
          {errorQuery ? (
            <Typography sx={{ fontSize: 13, color: 'error.main' }}>
              {errorQuery.message}
            </Typography>
          ) : null}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                lg: 'repeat(3, minmax(0, 1fr))'
              },
              gap: 1.5,
              maxHeight: 420,
              overflowY: 'auto',
              p: 0.5,
              m: -0.5
            }}>
            {visibleStores.map(store => {
              const selected = restaurant.includes(store._id)
              return (
                <Box
                  key={store._id}
                  role="checkbox"
                  aria-checked={selected}
                  tabIndex={0}
                  onClick={() => toggleStore(store._id)}
                  onKeyDown={e => {
                    if (e.key === ' ' || e.key === 'Enter') {
                      e.preventDefault()
                      toggleStore(store._id)
                    }
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 1.25,
                    borderRadius: '14px',
                    cursor: 'pointer',
                    userSelect: 'none',
                    border: '1.5px solid',
                    borderColor: selected
                      ? 'primary.main'
                      : 'rgba(16,24,40,0.08)',
                    bgcolor: selected ? '#FFF6F0' : 'common.white',
                    boxShadow: selected
                      ? '0 8px 18px -10px rgba(244,102,31,0.6)'
                      : '0 2px 6px -4px rgba(16,24,40,0.2)',
                    transition: 'all .15s ease',
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      borderColor: selected ? 'primary.main' : 'grey.400'
                    },
                    '&:focus-visible': {
                      outline: '2px solid',
                      outlineColor: 'primary.main'
                    }
                  }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      flexShrink: 0,
                      borderRadius: '10px',
                      overflow: 'hidden',
                      bgcolor: 'grey.100',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                    {store.image ? (
                      <img
                        src={store.image}
                        alt=""
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    ) : (
                      <StorefrontOutlined
                        sx={{ fontSize: 20, color: 'text.disabled' }}
                      />
                    )}
                  </Box>
                  <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                    <Typography
                      noWrap
                      sx={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: 'text.primary'
                      }}>
                      {store.name}
                    </Typography>
                    <Typography
                      noWrap
                      sx={{ fontSize: 12, color: 'text.disabled' }}>
                      {store.address}
                    </Typography>
                  </Box>
                  {selected ? (
                    <CheckCircleRounded
                      sx={{ color: 'primary.main', fontSize: 22 }}
                    />
                  ) : (
                    <RadioButtonUncheckedRounded
                      sx={{ color: 'grey.300', fontSize: 22 }}
                    />
                  )}
                </Box>
              )
            })}
          </Box>
          {data && visibleStores.length === 0 ? (
            <Typography sx={{ fontSize: 13, color: 'text.disabled', py: 2 }}>
              {t('NoStoresMatch')}
            </Typography>
          ) : null}
          <Box>
            <Button
              className={globalClasses.button}
              disabled={loading}
              onClick={async e => {
                e.preventDefault()
                if (onSubmitValidation() && !loading) {
                  mutate({
                    variables: {
                      section: {
                        _id: props.section ? props.section._id : '',
                        name: formRef.current['input-name'].value,
                        enabled: sectionEnable,
                        restaurants: restaurant
                      }
                    }
                  })
                  // Clear the form fields after submission
                  clearFields()
                  // Close the modal after 3 seconds
                  setTimeout(() => {
                    props.onClose && props.onClose() // Close the modal
                  }, 4000)
                }
              }}>
              {props.section ? t('Update') : t('Save')}
            </Button>
          </Box>
        </form>
        <Box mt={2}>
          {success && (
            <Alert
              className={globalClasses.alertSuccess}
              variant="filled"
              severity="success">
              {success}
            </Alert>
          )}
          {error && (
            <Alert
              className={globalClasses.alertError}
              variant="filled"
              severity="error">
              {error}
            </Alert>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default withTranslation()(Section)
